/**
 * Product Variant Manager Module
 * Handles product variants, attribute selection, and gallery updates
 */

import { escapeHtml } from '../core/utils.js';

export class ProductVariantManager {
    /**
     * @param {Object} system - The ECommerceSystem instance
     * @param {Object} [options] - Optional scoping for embedded use (e.g. category product cards)
     * @param {HTMLElement|Document} [options.root=document] - Scope for all DOM queries and events
     * @param {string} [options.mode='product'] - 'product' (single product page) or 'card' (scoped card)
     * @param {Object} [options.variantData=null] - Variant data (card mode), defaults to window.productVariantData
     */
    constructor(system, options = {}) {
        this.system = system;
        this.root = options.root || document;
        this.mode = options.mode || 'product';
        this.variantData = options.variantData || null;
        this.currentVariant = null;
        this.attributeSelections = new Map();
        this.gallerySlider = null;
        this.initialized = false;
    }

    init() {
        // Category pages are handled by CategoryVariantManager with one scoped instance
        // per product card - the single-product logic must not touch those pages
        if (this.mode === 'product' && typeof window.templateType !== 'undefined' && window.templateType === 'Category') {
            console.log('ProductVariantManager: Category page detected, deferring to CategoryVariantManager');
            return;
        }

        const variantData = this.variantData || (this.mode === 'product' ? window.productVariantData : null);
        const hasVariants = this.mode === 'product'
            ? window.hasVariants
            : (variantData && variantData.hasVariants === true);

        // Check if we have variant data
        if (!variantData || !hasVariants) {
            if (this.mode === 'product') {
                console.log('ProductVariantManager: No variant data found, setting up standard interface');
                this.setupStandardInterface();
            }
            return;
        }

        try {
            this.variantData = variantData;
            this.setupVariantInterface();
            this.setupGallery();
            this.setupEventListeners();
            this.selectDefaultVariant();
            this.initialized = true;
            console.log('ProductVariantManager: Successfully initialized with', this.variantData.variants?.length || 0, 'variants');
        } catch (error) {
            console.error('ProductVariantManager: Initialization failed:', error);
        }
    }

    setupStandardInterface() {
        // WARNING: this method uses document-wide queries, not this.root. On CMS
        // pages with ProductParts it hides the FIRST card's .js-variant-selectors;
        // CategoryVariantManager re-shows the selectors per card and runs AFTER
        // this method via system.js init order. That call order is load-bearing -
        // see the ORDER MATTERS comment in core/system.js.
        // For products without variants, show static info and hide variant info
        const staticInfo = document.querySelector('.js-static-info');
        const variantInfo = document.querySelector('.js-variant-info');
        const variantSelectors = document.querySelector('.js-variant-selectors');
        const standardGallery = document.querySelector('.js-standard-gallery');
        const variantGallery = document.querySelector('.js-variant-gallery');
        
        if (staticInfo) {
            staticInfo.style.display = 'block';
        }
        
        if (variantInfo) {
            variantInfo.style.display = 'none';
        }
        
        if (variantSelectors) {
            variantSelectors.style.display = 'none';
        }
        
        if (standardGallery) {
            standardGallery.style.display = 'block';
        }
        
        if (variantGallery) {
            variantGallery.style.display = 'none';
        }
        
        console.log('ProductVariantManager: Standard interface setup completed');
    }

    setupVariantInterface() {
        const selectorsContainer = this.root.querySelector('.js-variant-selectors');
        if (!selectorsContainer || !this.variantData.attributes) return;

        // Show variant selectors
        selectorsContainer.style.display = 'block';

        // For products with variants, hide static info and show variant info
        const staticInfo = this.root.querySelector('.js-static-info');
        const variantInfo = this.root.querySelector('.js-variant-info');
        const standardGallery = this.root.querySelector('.js-standard-gallery');
        const variantGallery = this.root.querySelector('.js-variant-gallery');
        
        if (staticInfo) {
            staticInfo.style.display = 'none';
        }
        
        if (variantInfo) {
            variantInfo.style.display = 'block';
        }
        
        if (standardGallery) {
            standardGallery.style.display = 'none';
        }
        
        if (variantGallery) {
            variantGallery.style.display = 'block';
        }
        
        // Generate attribute selectors
        let html = '';
        this.variantData.attributes.forEach(attribute => {
            html += this.generateAttributeSelector(attribute);
        });
        
        selectorsContainer.innerHTML = html;
        
        console.log('ProductVariantManager: Variant interface setup completed');
    }

    generateAttributeSelector(attribute) {
        // Read the data-show-images attribute from the variant-selectors container
        const selectorsContainer = this.root.querySelector('.js-variant-selectors');
        const showImagesMode = selectorsContainer?.getAttribute('data-show-images') || 'visual';

        // Determine selector type based on data-show-images and hasImages
        let selectorType;
        if (showImagesMode === 'select') {
            // Force select dropdown when explicitly set to "select"
            selectorType = 'select';
        } else if (showImagesMode === 'button') {
            // Force plain text buttons (no background images) when explicitly set to "button"
            selectorType = 'button';
        } else {
            // Default "visual" mode: use visual if images exist, otherwise fallback to select
            selectorType = attribute.hasImages ? 'visual' : 'select';
        }

        let html = `<div class="variant-attribute" data-attribute-id="${attribute.id}">`;

        // Button mode uses an action-style label ("Välj storlek"), other modes use "Storlek:"
        if (selectorType === 'button') {
            html += `<label class="variant-attribute-label">Välj ${escapeHtml(attribute.name.toLowerCase())}</label>`;
        } else {
            html += `<label class="variant-attribute-label">${escapeHtml(attribute.name)}:</label>`;
        }

        if (selectorType === 'select') {
            // Generate select dropdown for attributes without images
            html += `<select class="variant-attribute-select js-attribute-select" data-attribute-id="${attribute.id}">`;
            html += `<option value="">Välj ${attribute.name.toLowerCase()}...</option>`;

            attribute.values.forEach(value => {
                html += `<option value="${value.id}" data-value="${escapeHtml(value.value)}">${escapeHtml(value.value)}</option>`;
            });

            html += `</select>`;
        } else {
            // Generate buttons - with background images in "visual" mode, plain text in "button" mode
            html += `<div class="variant-attribute-visual js-attribute-visual" data-attribute-id="${attribute.id}">`;

            attribute.values.forEach(value => {
                const imageStyle = selectorType === 'visual' && value.images && value.images.length > 0
                    ? `style="background-image: url('${value.images[0]}');"`
                    : '';

                html += `<button type="button" class="variant-option js-variant-option"
                         data-attribute-id="${attribute.id}"
                         data-value-id="${value.id}"
                         data-value="${escapeHtml(value.value)}"
                         ${imageStyle}>
                         ${escapeHtml(value.value)}
                         </button>`;
            });

            html += `</div>`;
        }

        html += `</div>`;
        return html;
    }

    setupGallery() {
        const variantGallery = this.root.querySelector('.js-variant-gallery');
        const standardGallery = this.root.querySelector('.js-standard-gallery');
        
        if (variantGallery && standardGallery) {
            // Show variant gallery, hide standard gallery
            variantGallery.style.display = 'block';
            standardGallery.style.display = 'none';
        }
    }

    setupEventListeners() {
        // Listen for attribute selection changes (scoped to this.root so multiple
        // instances on the same page never react to each other's events)
        this.root.addEventListener('change', (e) => {
            if (e.target.classList.contains('js-attribute-select')) {
                this.handleAttributeSelection(e.target);
            }
        });

        this.root.addEventListener('click', (e) => {
            if (e.target.classList.contains('js-variant-option')) {
                this.handleVisualAttributeSelection(e.target);
            }
        });
    }

    handleAttributeSelection(selectElement) {
        const attributeId = selectElement.dataset.attributeId;
        const selectedOption = selectElement.options[selectElement.selectedIndex];
        const valueId = selectedOption.value;
        const value = selectedOption.dataset.value;

        if (valueId) {
            this.attributeSelections.set(attributeId, { valueId, value });
        } else {
            this.attributeSelections.delete(attributeId);
        }

        this.updateVariantSelection();
    }

    handleVisualAttributeSelection(button) {
        const attributeId = button.dataset.attributeId;
        const valueId = button.dataset.valueId;
        const value = button.dataset.value;

        // Update visual state
        const container = button.closest('.js-attribute-visual');
        container.querySelectorAll('.js-variant-option').forEach(btn => {
            btn.classList.remove('selected');
        });
        button.classList.add('selected');

        // Update selection
        this.attributeSelections.set(attributeId, { valueId, value });
        this.updateVariantSelection();
    }

    updateVariantSelection() {
        // Find matching variant based on current selections
        const matchingVariant = this.findMatchingVariant();
        
        if (matchingVariant) {
            this.selectVariant(matchingVariant);
        } else {
            this.clearVariantSelection();
        }

        // Update availability of other options
        this.updateOptionAvailability();
    }

    findMatchingVariant() {
        if (!this.variantData.variants || this.attributeSelections.size === 0) {
            return null;
        }

        return this.variantData.variants.find(variant => {
            // Check if all selected attributes match this variant
            for (const [attributeId, selection] of this.attributeSelections) {
                const attribute = this.variantData.attributes.find(attr => attr.id === attributeId);
                if (!attribute) continue;

                const attributeName = attribute.name.toLowerCase();
                const variantValue = variant.attributeValues[attributeName];
                
                if (variantValue !== selection.value) {
                    return false;
                }
            }
            return true;
        });
    }

    selectVariant(variant) {
        this.currentVariant = variant;
        this.updateProductInfo(variant);
        this.updateGallery(variant);
        this.updateAddToCartButton(variant);
        
        // Dispatch event for other modules (e.g., gift card form)
        const variantSelectedEvent = new CustomEvent('variantSelected', {
            detail: { variant: variant }
        });
        document.dispatchEvent(variantSelectedEvent);
    }

    clearVariantSelection() {
        this.currentVariant = null;
        this.hideVariantInfo();
        this.resetAddToCartButton();
    }

    updateProductInfo(variant) {
        // Update price with discount logic
        const priceElement = this.root.querySelector('.js-variant-price');
        const discountPriceSpan = this.root.querySelector('.js-discount-price');
        const originalPriceSpan = this.root.querySelector('.js-original-price');
        
        if (priceElement && discountPriceSpan && originalPriceSpan) {
            if (variant.hasDiscountPrice) {
                // Show discount price + strikethrough original
                discountPriceSpan.textContent = variant.formattedDiscountPrice;
                originalPriceSpan.textContent = variant.formattedPrice;
                discountPriceSpan.style.display = 'inline';
                originalPriceSpan.style.display = 'inline';
                originalPriceSpan.classList.add('original-price');  // Add strikethrough class
            } else {
                // Show only regular price WITHOUT strikethrough
                discountPriceSpan.style.display = 'none';
                originalPriceSpan.textContent = variant.formattedPrice;
                originalPriceSpan.style.display = 'inline';
                originalPriceSpan.classList.remove('original-price');  // Remove strikethrough class
            }
            priceElement.style.display = 'block';
        }

        // Update article number
        const articleElement = this.root.querySelector('.js-variant-article');
        const articleValue = this.root.querySelector('.js-variant-article .js-article-value');
        if (articleElement && articleValue) {
            articleValue.textContent = variant.articleNumber;
            articleElement.style.display = 'block';
        }

        // Update stock status
        const stockElement = this.root.querySelector('.js-variant-stock');
        const stockValue = this.root.querySelector('.js-variant-stock .js-stock-value');
        if (stockElement && stockValue) {
            const stockText = variant.isAvailable ? 
                `${variant.stockQuantity}` : 
                '0';
            stockValue.textContent = stockText;
            stockElement.style.display = 'block';
        }

        // Update main product info for cart compatibility
        this.updateMainProductInfo(variant);
    }

    updateMainProductInfo(variant) {
        // Update the main product info elements that the cart system uses
        const priceElement = this.root.querySelector('.js-cart-price');
        const idElement = this.root.querySelector('.js-cart-id');
        const nameElement = this.root.querySelector('.js-cart-name');
        const imageElement = this.root.querySelector('.js-cart-image');
        const productVariantIdElement = this.root.querySelector('.js-cart-productvariantid');
        
        if (priceElement) {
            // Use discount price if available, otherwise regular price
            const finalPrice = variant.hasDiscountPrice ? variant.discountPrice : variant.price;
            priceElement.textContent = finalPrice.toFixed(2);
        }
        
        if (idElement) {
            idElement.textContent = variant.articleNumber;
        }
        
        if (nameElement && variant.name) {
            nameElement.textContent = variant.name;
            console.log('ProductVariantManager: Updated ProductName to:', variant.name);
        }
        
        // CRITICAL: Update ProductVariantId for discount validation
        if (productVariantIdElement && variant.id) {
            productVariantIdElement.textContent = variant.id;
            console.log('ProductVariantManager: Updated ProductVariantId to:', variant.id);
        }
        
        // Update cart image element med variant-bild
        if (imageElement) {
            // Hämta första bilden från variant (samma logik som getVariantImages)
            const variantImages = this.getVariantImages(variant);
            if (variantImages.length > 0) {
                const firstImageUrl = this.transformImageUrl(variantImages[0]);
                imageElement.setAttribute('src', firstImageUrl);
                console.log('ProductVariantManager: Updated cart image to variant image:', firstImageUrl);
            } else {
                console.log('ProductVariantManager: No variant images found for', variant.name);
            }
        }

        // Update inventory data for cart compatibility
        const availableElement = this.root.querySelector('.js-cart-available');
        const stockElement = this.root.querySelector('.js-cart-stock');
        
        if (availableElement && variant.isAvailable !== undefined) {
            availableElement.textContent = variant.isAvailable ? 'True' : 'False';
        }
        
        if (stockElement && variant.stockQuantity !== undefined) {
            stockElement.textContent = variant.stockQuantity.toString();
        }
    }

    hideVariantInfo() {
        const infoElements = [
            '.js-variant-price',
            '.js-variant-article', 
            '.js-variant-stock'
        ];
        
        infoElements.forEach(selector => {
            const element = this.root.querySelector(selector);
            if (element) {
                element.style.display = 'none';
            }
        });
    }

    updateGallery(variant) {
        const sliderContainer = this.root.querySelector('.js-variant-slider');
        const navContainer = this.root.querySelector('.js-variant-nav');
        
        if (!sliderContainer || !navContainer) return;

        // Destroy existing slider if it exists
        if (this.gallerySlider && typeof $ !== 'undefined') {
            try {
                $(sliderContainer).slick('unslick');
            } catch (e) {
                // Ignore errors when destroying slider
            }
        }

        // Get images for this variant from attribute values
        const variantImages = this.getVariantImages(variant);
        
        if (variantImages.length === 0) {
            console.log('ProductVariantManager: No images found for variant', variant.name);
            return;
        }

        // Build gallery HTML
        let sliderHtml = '';
        let navHtml = '';
        
        variantImages.forEach(imageUrl => {
            const transformedUrl = this.transformImageUrl(imageUrl);
            const thumbUrl = this.getImagePath(imageUrl, 'thumb');
            
            sliderHtml += `
                <div class="gallery-item">
                    <div class="image-wrapper of-wrapper">
                        <img src="${transformedUrl}" alt="${escapeHtml(variant.name)}">
                    </div>
                </div>
            `;
            navHtml += `
                <div class="gallery-item">
                    <div class="image-wrapper of-wrapper">
                        <img src="${thumbUrl}" alt="${escapeHtml(variant.name)}">
                    </div>
                </div>
            `;
        });

        // Update containers
        sliderContainer.innerHTML = sliderHtml;
        navContainer.innerHTML = navHtml;

        // Initialize new slider if jQuery and Slick are available
        if (typeof $ !== 'undefined' && $.fn.slick) {
            try {
                $(sliderContainer).slick({
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    speed: 800,
                });

                // Setup thumbnail navigation
                $(navContainer).find('> div').click(function() {
                    $(sliderContainer).slick('slickGoTo', $(this).index());
                });

                this.gallerySlider = true;
            } catch (error) {
                console.error('ProductVariantManager: Failed to initialize gallery slider:', error);
            }
        }
    }

    getVariantImages(variant) {
        const images = [];
        const addedPaths = new Set();  // Add Set for duplicate control
        
        // 1. First: Get images from attribute values that match this variant
        if (this.variantData.attributes) {
            this.variantData.attributes.forEach(attribute => {
                if (attribute.hasImages && attribute.values) {
                    const attributeName = attribute.name.toLowerCase();
                    const variantValue = variant.attributeValues[attributeName];
                    
                    if (variantValue) {
                        const matchingValue = attribute.values.find(v => v.value === variantValue);
                        if (matchingValue && matchingValue.images && matchingValue.images.length > 0) {
                            matchingValue.images.forEach(img => {
                                const normalizedPath = this.normalizeImagePath(img);
                                if (!addedPaths.has(normalizedPath)) {
                                    addedPaths.add(normalizedPath);
                                    images.push(img);
                                }
                            });
                        }
                    }
                }
            });
        }
        
        // 2. Then: Add variant's own images if any (only if not already added from attributes)
        if (variant.images && variant.images.length > 0) {
            variant.images.forEach(img => {
                const normalizedPath = this.normalizeImagePath(img);
                if (!addedPaths.has(normalizedPath)) {
                    addedPaths.add(normalizedPath);
                    images.push(img);
                }
            });
        }
        
        // 3. Finally: Add product images from DOM (general product images like environment shots)
        // These come AFTER variant-specific images
        this.addProductImages(images, addedPaths);
        
        return images;
    }

    normalizeImagePath(path) {
        // Normalize URL for duplicate control
        // Remove all common prefixes and get just the unique path part
        if (!path) return '';
        
        // Remove common prefixes
        let normalized = path
            .replace(/^\/assets\/cart\//, '')  // Remove /assets/cart/
            .replace(/^\/assets\//, '')         // Remove /assets/
            .replace(/^cart\//, '')             // Remove cart/
            .replace(/^\//, '');                // Remove leading slash
        
        // Also remove size folders (thumb, medium, large) for better duplicate detection
        normalized = normalized
            .replace(/\/thumb\//, '/')
            .replace(/\/medium\//, '/')
            .replace(/\/large\//, '/');
        
        return normalized;
    }

    addProductImages(images, addedPaths) {
        // Collect ALL images that belong to ANY attribute value (variant-specific images).
        // These must NOT be treated as product-level images, even if they appear in the DOM.
        // This prevents the first variant's image from bleeding into other variants' galleries
        // when the server-side OrderedImages fallback renders it in the standard gallery.
        const allAttributeImages = new Set();
        if (this.variantData && this.variantData.attributes) {
            this.variantData.attributes.forEach(attribute => {
                if (attribute.hasImages && attribute.values) {
                    attribute.values.forEach(value => {
                        if (value.images) {
                            value.images.forEach(img => {
                                allAttributeImages.add(this.normalizeImagePath(img));
                            });
                        }
                    });
                }
            });
        }

        // Get product images from the DOM (from ForEachImage tokens).
        // Only add images that are NOT variant-specific (i.e. not in any attribute value's image list).
        const productImageElements = this.root.querySelectorAll('.js-standard-gallery .gallery-item img');
        productImageElements.forEach(img => {
            const imageSrc = img.getAttribute('src');
            if (imageSrc) {
                const normalizedPath = this.normalizeImagePath(imageSrc);
                if (!allAttributeImages.has(normalizedPath) && !addedPaths.has(normalizedPath)) {
                    addedPaths.add(normalizedPath);
                    images.push(imageSrc);
                }
            }
        });
    }

    transformImageUrl(url) {
        // Transform image URL to match the expected format
        // Do NOT transform if URL already starts with /assets/cart or /resources
        if (!url) return url;
        
        if (url.startsWith('/resources') || url.startsWith('/assets/cart')) {
            // Already has correct prefix, don't transform
            return url;
        }
        
        if (url.startsWith('cart/')) {
            // Add /assets/ prefix
            return '/assets/' + url;
        }
        
        // For backwards compatibility: if no prefix at all, add /assets/cart
        if (!url.startsWith('/')) {
            return '/assets/cart/' + url;
        }
        
        return url;
    }

    getImagePath(url, sizeFolder) {
        // Gets image path for a specific size variant (medium, thumb, large)
        // Transforms: cart/product-images/000073/image.jpg -> cart/product-images/000073/thumb/image.jpg
        if (!url) return url;

        try {
            // First transform the URL to standard format
            const transformedUrl = this.transformImageUrl(url);
            
            // Check if URL already contains a size folder - if so, return as-is
            if (transformedUrl.includes('/thumb/') || 
                transformedUrl.includes('/medium/') || 
                transformedUrl.includes('/large/')) {
                return transformedUrl; // Already has a size folder, don't modify
            }
            
            // Split path into parts
            // Example: /assets/cart/product-images/000073/image.jpg
            const parts = transformedUrl.split('/');
            if (parts.length < 2) {
                return transformedUrl; // Not enough parts, return as-is
            }

            // Get filename (last part)
            const filename = parts[parts.length - 1];
            
            // Get directory (everything except filename)
            const directory = parts.slice(0, -1).join('/');
            
            // Build new path: /assets/cart/product-images/000073/thumb/image.jpg
            return `${directory}/${sizeFolder}/${filename}`;
        } catch (error) {
            console.error('ProductVariantManager: Failed to build image path:', error);
            return url; // Return original on error
        }
    }

    updateAddToCartButton(variant) {
        const button = this.root.querySelector('.js-add-to-cart');
        if (!button) return;

        if (variant.isAvailable) {
            button.disabled = false;
            button.textContent = 'Lägg i varukorg';
            button.classList.remove('disabled');
        } else {
            button.disabled = true;
            button.textContent = 'Ej tillgänglig';
            button.classList.add('disabled');
        }
    }

    resetAddToCartButton() {
        const button = this.root.querySelector('.js-add-to-cart');
        if (!button) return;

        button.disabled = false;
        button.textContent = 'Lägg i varukorg';
        button.classList.remove('disabled');
    }

    updateOptionAvailability() {
        // This would implement logic to disable options that don't lead to valid variants
        // For now, we'll keep it simple and enable all options
        this.root.querySelectorAll('.js-variant-option').forEach(option => {
            option.classList.remove('disabled');
            option.disabled = false;
        });
    }

    selectDefaultVariant() {
        if (!this.variantData.defaultVariantId || !this.variantData.variants) return;

        const defaultVariant = this.variantData.variants.find(v => v.id === this.variantData.defaultVariantId);
        if (!defaultVariant) return;

        // Set default selections based on the default variant
        this.variantData.attributes.forEach(attribute => {
            const attributeName = attribute.name.toLowerCase();
            const variantValue = defaultVariant.attributeValues[attributeName];
            
            if (variantValue) {
                const value = attribute.values.find(v => v.value === variantValue);
                if (value) {
                    this.attributeSelections.set(attribute.id, { 
                        valueId: value.id, 
                        value: value.value 
                    });
                    
                    // Update UI
                    const selectElement = this.root.querySelector(`select[data-attribute-id="${attribute.id}"]`);
                    if (selectElement) {
                        selectElement.value = value.id;
                    }

                    const visualButton = this.root.querySelector(`button[data-attribute-id="${attribute.id}"][data-value-id="${value.id}"]`);
                    if (visualButton) {
                        visualButton.classList.add('selected');
                    }
                }
            }
        });

        // Select the default variant
        this.selectVariant(defaultVariant);
    }

    // Public methods
    getCurrentVariant() {
        return this.currentVariant;
    }

    getSelectedAttributes() {
        return Object.fromEntries(this.attributeSelections);
    }

    isInitialized() {
        return this.initialized;
    }
}

// Export for global access
export default ProductVariantManager;
